import * as React from "react"
import { type DialogProps } from "@radix-ui/react-dialog"
import { Command as CommandPrimitive } from "cmdk"
import { Search } from "lucide-react"

import { cn } from "@/lib/utils"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import {
  menuItem,
  menuGroupLabel,
  menuKbd,
  menuSeparator,
} from "@/lib/menu-classes"

const Command = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive>
>(({ className, ...props }, ref) => (
  <CommandPrimitive
    ref={ref}
    className={cn(
      "flex h-full w-full flex-col overflow-hidden rounded-[var(--menu-radius)] bg-popover text-popover-foreground",
      className
    )}
    {...props}
  />
))
Command.displayName = CommandPrimitive.displayName

interface CommandDialogProps extends DialogProps {}

const CommandDialog = ({ children, ...props }: CommandDialogProps) => {
  return (
    <Dialog {...props}>
      <DialogContent overlayClassName="gio-static" className="overflow-hidden p-0 shadow-[var(--menu-shadow)] sm:max-w-[640px] sm:min-w-[540px] gio-static">
        <Command>{children}</Command>
      </DialogContent>
    </Dialog>
  )
}

const CommandInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input> & {
    /** §12: while results load, the search icon becomes a 13px spinner. */
    loading?: boolean
  }
>(({ className, loading, ...props }, ref) => (
  <div
    className="flex items-center border-b border-[hsl(var(--tbl-divider-color))] px-3"
    cmdk-input-wrapper=""
  >
    {loading ? (
      <span className="mr-2 inline-flex h-4 w-4 shrink-0 items-center justify-center text-[hsl(var(--menu-group-color))]" aria-hidden>
        <span className="gio-spinner gio-spinner-current" style={{ width: 13, height: 13 }} />
      </span>
    ) : (
      <Search className="mr-2 h-4 w-4 shrink-0 text-[hsl(var(--menu-group-color))]" />
    )}
    <CommandPrimitive.Input
      ref={ref}
      className={cn(
        "flex h-10 w-full rounded-[var(--menu-radius)] bg-transparent py-3 text-form-value font-inter outline-none placeholder:text-[hsl(var(--menu-group-color))] disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      {...props}
    />
  </div>
))
CommandInput.displayName = CommandPrimitive.Input.displayName

/**
 * §7 Combobox: the list keeps the height it opened with while you type (it never
 * shrinks under the cursor; "No results" and "Create '…'" fill the same box), and
 * neither typing nor the highlight animates.
 */
const CommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className, style, ...props }, ref) => {
  const local = React.useRef<HTMLDivElement | null>(null)
  const [minHeight, setMinHeight] = React.useState<number>()
  React.useLayoutEffect(() => {
    const el = local.current
    if (!el) return
    // Measure once the first results have rendered.
    const frame = requestAnimationFrame(() => {
      const h = el.offsetHeight // layout size, unaffected by the popover's grow-in scale
      if (h > 0) setMinHeight(h)
    })
    return () => cancelAnimationFrame(frame)
  }, [])
  return (
    <CommandPrimitive.List
      ref={(node) => {
        local.current = node
        if (typeof ref === 'function') ref(node)
        else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node
      }}
      className={cn("max-h-[300px] overflow-y-auto overflow-x-hidden p-[var(--menu-pad)]", className)}
      style={minHeight ? { minHeight, ...style } : style}
      {...props}
    />
  )
})
CommandList.displayName = CommandPrimitive.List.displayName

const CommandEmpty = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Empty>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>((props, ref) => (
  <CommandPrimitive.Empty
    ref={ref}
    className="py-6 text-center text-body-sm text-text-tertiary"
    {...props}
  />
))
CommandEmpty.displayName = CommandPrimitive.Empty.displayName

const CommandGroup = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Group
    ref={ref}
    className={cn(
      "overflow-hidden text-foreground",
      // Gio Docs' menu header: 12px sentence case on a 28px line.
      "[&_[cmdk-group-heading]]:flex [&_[cmdk-group-heading]]:min-h-[28px] [&_[cmdk-group-heading]]:items-center [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1",
      "[&_[cmdk-group-heading]]:text-menu-head [&_[cmdk-group-heading]]:font-inter",
      "[&_[cmdk-group-heading]]:[color:hsl(var(--menu-group-color))]",
      className
    )}
    {...props}
  />
))
CommandGroup.displayName = CommandPrimitive.Group.displayName

const CommandSeparator = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Separator
    ref={ref}
    className={cn(menuSeparator, className)}
    {...props}
  />
))
CommandSeparator.displayName = CommandPrimitive.Separator.displayName

const CommandItem = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item>
>(({ className, value, children, ...props }, ref) => {
  const fallbackId = React.useId()
  const fallbackValue = React.useMemo(() => {
    if (typeof value === "string") return value
    if (typeof children === "string") return children

    const text = React.Children.toArray(children)
      .map((child) => {
        if (typeof child === "string" || typeof child === "number") return String(child)
        if (React.isValidElement(child)) {
          const childProps = child.props as { children?: React.ReactNode }
          return typeof childProps.children === "string" ? childProps.children : ""
        }
        return ""
      })
      .join(" ")
      .trim()

    return text || props.id || fallbackId
  }, [children, fallbackId, props.id, value])

  return (
    <CommandPrimitive.Item
      ref={ref}
      value={fallbackValue}
      className={cn(menuItem, className)}
      {...props}
    >
      {children}
    </CommandPrimitive.Item>
  )
})
CommandItem.displayName = CommandPrimitive.Item.displayName

const CommandShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return <span className={cn(menuKbd, className)} {...props} />
}
CommandShortcut.displayName = "CommandShortcut"

export {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
}
